import { createReducer } from 'redux-starter-kit';
import { selectRequest } from '../../../domain/requests/RequestReducer';
import {
  editDamageType, cancelEditDamageType, fetchAddDamageType,
  fetchUpdateDamageType, addPublishDamageType, removePublishDamageType,
  fetchDamageTypes, fetchDamageTypeReferences,
} from './damageTypeUiActions';

/**
 * A selector for those damage type reference areas in the domain.
 * @param {state} state The redux state for the application.
 */
export const selectDamageTypeReferencesMap = state => state.via.domain.damageTypeReferences;

/**
 * A selector for finding the current edit state.
 * @param {object} state The redux state for the application.
 * @returns An object containing details about the damage type being edited.
 * @example const editState = selectEditState(state);
 */
export const selectEditState = state => state.via.ui.damageTypes.editState;

/**
 * A selector for finding the current publishing type codes.
 * @param {object} state The redux state of the application.
 * @returns An array of damage type codes.
 * @example const loadingTypeCodes = selectLoadingDamageTypeCodes(state);
 */
export const selectLoadingDamageTypeCodes = state => state.via.ui.damageTypes.pendingPublishDamageTypes;

/**
 * A selector that returns the state of the 'FETCH_DAMAGE_TYPES' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 * @example const damageTypeRequest = selectDamageTypesRequest(state);
 */
export const selectDamageTypesRequest = (state) => {
  const requestId = state.via.ui.damageTypes.requestDamageTypesId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector for the current 'FETCH_CREATE_DAMAGE_TYPE' request.
 * @param {object} state The redux state for the application.
 * @returns An object representing the request state.
 * @example const addDamageTypesRequest = selectAddDamageTypeRequest(state);
 */
export const selectSubmitDamageTypeRequest = (state) => {
  const requestId = state.via.ui.damageTypes.editState.requestSubmitDamageTypeId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector that returns the state of the 'FETCH_DAMAGE_TYPE_REFERENCES' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 * @example const damageTypeReferencesRequest = selecDamageTypeReferencesRequest(state);
 */
export const selectDamageTypeReferencesRequest = (state) => {
  const requestId = state.via.ui.damageTypes.requestDamageTypeReferencesId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector for those damage type reference areas loaded by the Damage Type Configuration screen.
 * @param {state} state The redux state for the application.
 * @returns The damage type references areas that were loaded.
 * @example const damageTypeReferences = selectDamageTypeReferences(state);
 */
export const selectDamageTypeReferences = (state) => {
  const damageTypeReferencesMap = selectDamageTypeReferencesMap(state);
  const damageTypeReferences = Object.values(damageTypeReferencesMap);

  return damageTypeReferences || [];
};

const intitialEditingState = {
  isEditing: false,
  damageTypeCode: null,
  requestSubmitDamageTypeId: null,
};

const damageTypesInitialState = {
  editState: intitialEditingState, // Is a damage type being edited/created.
  pendingPublishDamageTypes: [], // Damage types being submitted for publishing.
  requestDamageTypesId: null, // The request id for loading damage types.
  requestDamageTypeReferencesId: null, // The request id for loading those areas that are referenced by a damage type.
};

/**
 * A reducer for handling the UI state changes for the Damage Type Configuration screen.
 */
const damageTypesReducer = createReducer(damageTypesInitialState, {
  // The user has started editing the damage type.
  [editDamageType]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      isEditing: true,
      damageTypeCode: action.payload,
    },
  }),

  // The user has stopped editing the damage type.
  [cancelEditDamageType]: state => ({
    ...state,
    editState: {
      ...intitialEditingState,
    },
  }),

  // A new requst has been made to add a damage type.
  [fetchAddDamageType]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestSubmitDamageTypeId: action.payload,
    },
  }),

  // A new request has been made to update a damage Type.
  [fetchUpdateDamageType]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestSubmitDamageTypeId: action.payload,
    },
  }),

  // A damage type is being published. So add it to state.
  [addPublishDamageType]: (state, action) => ({
    ...state,
    pendingPublishDamageTypes: [...state.pendingPublishDamageTypes, action.payload],
  }),

  // A damage type has finished publishing. Remove it from state.
  [removePublishDamageType]: (state, action) => ({
    ...state,
    pendingPublishDamageTypes: state.pendingPublishDamageTypes.filter(code => `${code}` !== `${action.payload}`),
  }),

  // A new request has been made for the damage types.
  [fetchDamageTypes]: (state, action) => ({
    ...state,
    requestDamageTypesId: action.payload,
  }),

  // A new request has been made for those areas referenced by a damage type.
  [fetchDamageTypeReferences]: (state, action) => ({
    ...state,
    requestDamageTypeReferencesId: action.payload,
  }),

});

export default damageTypesReducer;
